import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Select from "react-select";
import api from "../../api";
import svblogo from "../../assets/svblogo.png";
import authorizedSignature from "../../assets/MPS.png";
import "./InvoicePage.css";

const COMPANY_INFO = {
  SVB: {
    name: "Softvolute Buildcon LLP",
    address: "Update company address here",
    email: "financesvbllp@gmail.com",
    phone: "9898608595",
    gstin: "24ADGFS5901BIZ1",
  },
};

// GSTIN is copied into each invoice so older invoices retain their issued details.
const normalizeGstin = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();
const clientMasterGstin = (client) =>
  normalizeGstin(
    client?.gstin ||
      client?.gstIN ||
      client?.gstNumber ||
      client?.gstNo ||
      client?.gstinNumber ||
      "",
  );

const UNIT_OPTIONS = [
  "Sq. Ft.",
  "Sq. M.",
  "Running Ft.",
  "Running M.",
  "Acre",
  "Yard",
  "Nos.",
  "Hours",
  "Days",
  "Lump Sum",
  "Percentage",
];

const emptyItem = (section = "Architecture") => ({
  section,
  service: "",
  serviceName: "",
  description: "",
  quantity: 1,
  unit: "Sq. Ft.",
  rate: 0,
  discountType: "none",
  discountValue: 0,
  gstRate: 18,
  hsnCode: "",
});

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);

const formatAddress = (address) => {
  if (!address) return "";

  if (typeof address === "string") {
    return address;
  }

  if (typeof address === "object") {
    return [
      address.address,
      address.city,
      address.state,
      address.country,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return "";
};

const cleanSectionName = (value = "") =>
  String(value).trim().replace(/\s+/g, " ");

const getServiceId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
};

const SEARCH_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderColor: state.isFocused ? "#94a3b8" : "#dbe3ee",
    boxShadow: state.isFocused ? "0 0 0 1px #94a3b8" : "none",
    fontSize: 13,
    borderRadius: 8,
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 36,
    padding: "0 10px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 36,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
    fontSize: 13,
  }),
};

const SearchableSelect = ({
  value,
  options,
  onChange,
  placeholder = "Select",
  isClearable = true,
}) => {
  const selectedOption =
    options.find((option) => String(option.value) === String(value || "")) ||
    null;

  return (
    <Select
      value={selectedOption}
      options={options}
      onChange={(option) => onChange(option?.value || "")}
      placeholder={placeholder}
      isSearchable
      isClearable={isClearable}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      noOptionsMessage={({ inputValue }) =>
        inputValue ? `No result for "${inputValue}"` : "No options"
      }
      styles={SEARCH_SELECT_STYLES}
    />
  );
};

const InvoicePage = () => {
  const previewRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [termsMaster, setTermsMaster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: "NEFT",
    amount: "",
    paymentDate: today(),
    senderName: "",
    senderBankName: "",
    senderAccountNumber: "",
    transactionNumber: "",
    chequeNumber: "",
    chequeDate: "",
    chequeBankName: "",
    remarks: "",
    proofFile: null,
  });
  const getPaymentProofUrl = (proofUrl = "") => {
    const value = String(proofUrl || "").trim();

    if (!value) return "";

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:") ||
      value.startsWith("blob:")
    ) {
      return value;
    }

    const apiBase = String(api.defaults?.baseURL || "").replace(/\/+$/, "");

    let serverBase = apiBase;

    if (/\/api$/i.test(serverBase)) {
      serverBase = serverBase.replace(/\/api$/i, "");
    }

    if (!/^https?:\/\//i.test(serverBase)) {
      serverBase = window.location.origin;
    }

    return `${serverBase}${value.startsWith("/") ? "" : "/"}${value}`;
  };
  const getCurrentFinancialYearStart = () => {
    const now = new Date();
    const year = now.getFullYear();
    return now.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearForDate = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    return date.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearLabel = (startYear) =>
    `${startYear}-${String(Number(startYear) + 1).slice(-2)}`;

  const getCalendarYearForFinancialMonth = (financialYearStart, month) =>
    Number(month) >= 4
      ? Number(financialYearStart)
      : Number(financialYearStart) + 1;

  const getCurrentFinancialQuarter = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 4 && month <= 6) return "1";
    if (month >= 7 && month <= 9) return "2";
    if (month >= 10 && month <= 12) return "3";
    return "4";
  };

  const [invoiceFilters, setInvoiceFilters] = useState({
    viewBy: "month",
    financialYear: String(getCurrentFinancialYearStart()),
    month: String(new Date().getMonth() + 1),
    quarter: getCurrentFinancialQuarter(),
    fromDate: "",
    toDate: "",
  });

  const logo = svblogo;

  const [form, setForm] = useState({
    company: "SVB",
    invoiceNumber: "",
    client: "",
    clientGstin: "",
    projectName: "",
    subject: "",
    documentDate: today(),
    dueDate: "",
    taxMode: "CGST_SGST",
    discountMode: "item",
    overallDiscountType: "none",
    overallDiscountValue: 0,
    bank: "",
    items: [emptyItem()],
    terms: [],
    status: "Draft",
    paidAmount: 0,
  });

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    [],
  );

  const load = async () => {
    try {
      setLoading(true);
      const [d, c, p, s, b, t] = await Promise.all([
        api.get("/sales/documents?type=invoice", { headers }),
        api.get("/sales/clients", { headers }),
        api.get("/projects", { headers }),
        api.get("/sales/services", { headers }),
        api.get("/sales/banks", { headers }),
        api.get("/sales/terms", { headers }),
      ]);

      setDocs(d.data || []);
      setClients(c.data || []);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setServices(s.data || []);
      setBanks(b.data || []);
      setTermsMaster(t.data || []);
    } catch (error) {
      console.error("Failed to load invoice data:", error);
      alert(error.response?.data?.message || "Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invoiceFinancialYears = useMemo(() => {
    const years = new Set();

    docs.forEach((document) => {
      if (!document.documentDate) return;

      const financialYear = getFinancialYearForDate(document.documentDate);
      if (financialYear !== null) years.add(financialYear);
    });

    years.add(getCurrentFinancialYearStart());

    return Array.from(years).sort((a, b) => b - a);
  }, [docs]);

  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const filteredDocs = useMemo(() => {
    return docs.filter((document) => {
      if (!document.documentDate) return false;

      const invoiceDate = new Date(document.documentDate);
      if (Number.isNaN(invoiceDate.getTime())) return false;

      const financialYearStart = Number(
        invoiceFilters.financialYear || getCurrentFinancialYearStart(),
      );

      if (invoiceFilters.viewBy === "month") {
        const selectedMonth = Number(invoiceFilters.month || 4);
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          selectedMonth,
        );

        if (invoiceDate.getFullYear() !== calendarYear) return false;
        if (invoiceDate.getMonth() + 1 !== selectedMonth) return false;
      }

      if (invoiceFilters.viewBy === "quarter") {
        const quarter = Number(invoiceFilters.quarter || 1);

        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        if (invoiceDate < range.from || invoiceDate > range.to) return false;
      }

      if (invoiceFilters.viewBy === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);

        if (invoiceDate < from || invoiceDate > to) return false;
      }

      if (invoiceFilters.viewBy === "custom") {
        if (invoiceFilters.fromDate) {
          const from = new Date(`${invoiceFilters.fromDate}T00:00:00`);
          if (invoiceDate < from) return false;
        }

        if (invoiceFilters.toDate) {
          const to = new Date(`${invoiceFilters.toDate}T23:59:59.999`);
          if (invoiceDate > to) return false;
        }
      }

      return true;
    });
  }, [docs, invoiceFilters]);

  const filteredInvoiceValue = useMemo(
    () =>
      filteredDocs.reduce(
        (sum, document) => sum + Number(document.grandTotal || 0),
        0,
      ),
    [filteredDocs],
  );

  const clearInvoiceFilters = () => {
    setInvoiceFilters({
      viewBy: "month",
      financialYear: String(getCurrentFinancialYearStart()),
      month: String(new Date().getMonth() + 1),
      quarter: getCurrentFinancialQuarter(),
      fromDate: "",
      toDate: "",
    });
  };

  const selectedClient = useMemo(
    () => clients.find((c) => c._id === form.client),
    [clients, form.client],
  );

  const selectedProject = useMemo(
    () => projects.find((project) => project.name === form.projectName),
    [projects, form.projectName],
  );

  const filteredProjects = useMemo(() => {
    if (!selectedClient) return projects;

    const selectedClientName = String(selectedClient.companyName || "")
      .trim()
      .toLowerCase();

    if (!selectedClientName) return projects;

    const matchedProjects = projects.filter(
      (project) =>
        String(project.clientName || "")
          .trim()
          .toLowerCase() === selectedClientName,
    );

    return matchedProjects.length ? matchedProjects : projects;
  }, [projects, selectedClient]);

  const filteredBanks = useMemo(
    () => banks.filter((b) => !b.company || b.company === "SVB"),
    [banks],
  );

  const selectedBank = useMemo(
    () => banks.find((b) => b._id === form.bank),
    [banks, form.bank],
  );

  const companyInfo = COMPANY_INFO.SVB;

  const waitForNextPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

  const uploadInvoicePdf = async (documentId, pdfBlob) => {
    if (!documentId || !pdfBlob) {
      throw new Error("Invoice PDF data is missing");
    }

    const fd = new FormData();
    fd.append("pdf", pdfBlob, `Invoice-${documentId}.pdf`);

    const response = await api.post(`/sales/documents/${documentId}/pdf`, fd, {
      headers: {
        ...headers,
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  };

  const generateAndSaveInvoicePdf = async ({ documentId, documentData }) => {
    if (!documentId) {
      throw new Error("Invoice ID is required to save PDF");
    }

    /*
      Load the exact saved invoice data into the existing preview UI.
      We keep the modal open only internally while generating the PDF.
    */
    setEditing(documentId);
    setForm({
      ...documentData,
      company: "SVB",
      invoiceNumber: documentData.invoiceNumber || documentData.number || "",
      client: documentData.client?._id || documentData.client || "",
      clientGstin: normalizeGstin(
        documentData.clientGstin ?? clientMasterGstin(documentData.client),
      ),
      bank: documentData.bank?._id || documentData.bank || "",
      projectName: documentData.projectName || "",
      subject: documentData.subject || "",
      documentDate: documentData.documentDate?.slice?.(0, 10) || today(),
      dueDate: documentData.dueDate?.slice?.(0, 10) || "",
      taxMode: documentData.taxMode || "CGST_SGST",
      discountMode:
        documentData.discountMode ||
        (documentData.overallDiscountType &&
        documentData.overallDiscountType !== "none" &&
        Number(documentData.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: documentData.overallDiscountType || "none",
      overallDiscountValue: Number(documentData.overallDiscountValue || 0),
      items: documentData.items?.length
        ? documentData.items.map((item) => ({
            ...item,
            section: cleanSectionName(item.section || "General"),
          }))
        : [emptyItem("Architecture")],
      terms: documentData.terms || [],
      status: documentData.status || "Draft",
      paidAmount: Number(documentData.paidAmount || 0),
    });

    setShowPreview(true);
    setOpen(true);

    await waitForNextPaint();

    if (!previewRef.current) {
      throw new Error("Invoice preview is not ready for PDF generation");
    }

    const canvas = await html2canvas(previewRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imageData = canvas.toDataURL("image/jpeg", 0.98);

    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();

    const pageHeight = pdf.internal.pageSize.getHeight();

    const imageWidth = pageWidth;

    const imageHeight = (canvas.height * imageWidth) / canvas.width;

    let heightLeft = imageHeight;

    let position = 0;

    pdf.addImage(
      imageData,
      "JPEG",
      0,
      position,
      imageWidth,
      imageHeight,
      undefined,
      "FAST",
    );

    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imageHeight;

      pdf.addPage();

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        position,
        imageWidth,
        imageHeight,
        undefined,
        "FAST",
      );

      heightLeft -= pageHeight;
    }

    const pdfBlob = pdf.output("blob");

    await uploadInvoicePdf(documentId, pdfBlob);

    return true;
  };

  const sections = useMemo(() => {
    const names = [];

    (form.items || []).forEach((item) => {
      const section = cleanSectionName(item.section || "General");
      if (section && !names.includes(section)) {
        names.push(section);
      }
    });

    return names.length ? names : ["Architecture"];
  }, [form.items]);

  const automaticTermsBySection = useMemo(() => {
    return sections
      .map((section) => {
        const normalizedSection = cleanSectionName(section);

        const matchedRows = termsMaster.filter(
          (row) =>
            String(row.service || "")
              .trim()
              .toLowerCase() === normalizedSection.toLowerCase(),
        );

        const terms = Array.from(
          new Set(
            matchedRows.flatMap((row) =>
              Array.isArray(row.terms) ? row.terms : [],
            ),
          ),
        ).filter(Boolean);

        return {
          section: normalizedSection,
          title: matchedRows[0]?.title || "Terms & Conditions",
          terms,
        };
      })
      .filter((group) => group.terms.length);
  }, [sections, termsMaster]);

  const automaticTerms = useMemo(
    () =>
      Array.from(
        new Set(
          automaticTermsBySection.flatMap((sectionGroup) => sectionGroup.terms),
        ),
      ),
    [automaticTermsBySection],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let itemDiscount = 0;
    let itemTaxBeforeOverall = 0;

    const mode = form.discountMode || "item";

    (form.items || []).forEach((item) => {
      const qty = Number(item.quantity || 0);
      const rate = Number(item.rate || 0);
      const base = qty * rate;

      let discount = 0;

      // Item discount is applied ONLY when Item Wise mode is selected.
      if (mode === "item") {
        if (item.discountType === "percentage") {
          discount = (base * Number(item.discountValue || 0)) / 100;
        } else if (item.discountType === "amount") {
          discount = Number(item.discountValue || 0);
        }
      }

      discount = Math.min(Math.max(discount, 0), base);

      const taxable = base - discount;
      const tax =
        form.taxMode === "NONE"
          ? 0
          : (taxable * Number(item.gstRate || 0)) / 100;

      subtotal += base;
      itemDiscount += discount;
      itemTaxBeforeOverall += tax;
    });

    const afterItemDiscount = subtotal - itemDiscount;

    let overallDiscount = 0;

    // Total invoice discount is applied ONLY when Total Discount mode is selected.
    if (mode === "total") {
      if (form.overallDiscountType === "percentage") {
        overallDiscount =
          (afterItemDiscount * Number(form.overallDiscountValue || 0)) / 100;
      } else if (form.overallDiscountType === "amount") {
        overallDiscount = Number(form.overallDiscountValue || 0);
      }
    }

    overallDiscount = Math.min(Math.max(overallDiscount, 0), afterItemDiscount);

    const taxableAmount = Math.max(0, afterItemDiscount - overallDiscount);

    // Overall discount proportionally reduces the taxable value and therefore GST.
    const ratio = afterItemDiscount > 0 ? taxableAmount / afterItemDiscount : 1;

    const taxTotal = form.taxMode === "NONE" ? 0 : itemTaxBeforeOverall * ratio;

    const cgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const sgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const igst = form.taxMode === "IGST" ? taxTotal : 0;

    return {
      subtotal,
      itemDiscount,
      overallDiscount,
      discountTotal: itemDiscount + overallDiscount,
      taxableAmount,
      taxTotal,
      cgst,
      sgst,
      igst,
      grandTotal: taxableAmount + taxTotal,
    };
  }, [form]);

  const reset = () => {
    setEditing(null);
    setShowPreview(false);
    setForm({
      company: "SVB",
      invoiceNumber: "",
      client: "",
      projectName: "",
      subject: "",
      documentDate: today(),
      dueDate: "",
      taxMode: "CGST_SGST",
      discountMode: "item",
      overallDiscountType: "none",
      overallDiscountValue: 0,
      bank: "",
      items: [emptyItem()],
      terms: [],
      status: "Draft",
      paidAmount: 0,
    });
  };

  const updateItem = (index, patch) => {
    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const addSection = () => {
    const name = cleanSectionName(newSectionName);

    if (!name) {
      alert("Enter section name, for example Architecture or Interior");
      return;
    }

    if (
      sections.some((section) => section.toLowerCase() === name.toLowerCase())
    ) {
      alert("This section already exists");
      return;
    }

    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(name)],
    }));
    setNewSectionName("");
  };

  const addServiceToSection = (section) => {
    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(section)],
    }));
  };

  const renameSection = (oldName, newName) => {
    const cleaned = cleanSectionName(newName);
    if (!cleaned) return;

    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item) =>
        cleanSectionName(item.section || "General") === oldName
          ? { ...item, section: cleaned }
          : item,
      ),
    }));
  };

  const removeSection = (section) => {
    const sectionItems = form.items.filter(
      (item) => cleanSectionName(item.section || "General") === section,
    );

    if (
      !window.confirm(
        `Remove "${section}" section and its ${sectionItems.length} service item(s)?`,
      )
    ) {
      return;
    }

    setForm((previous) => {
      const remaining = previous.items.filter(
        (item) => cleanSectionName(item.section || "General") !== section,
      );

      return {
        ...previous,
        items: remaining.length ? remaining : [emptyItem("Architecture")],
      };
    });
  };

  const chooseService = (index, serviceId) => {
    const service = services.find((s) => s._id === serviceId);
    if (!service) {
      updateItem(index, { service: "", serviceName: "" });
      return;
    }

    updateItem(index, {
      service: service._id,
      serviceName: service.name,
      description: service.description || "",
      unit: service.unit || "Sq. Ft.",
      rate: Number(service.defaultRate || 0),
      gstRate: Number(service.gstRate ?? 18),
    });
  };

  const validate = () => {
    if (!form.company) return "Select company";
    if (!String(form.invoiceNumber || "").trim()) return "Enter Invoice No.";
    if (!form.client) return "Select client";
    if (
      form.clientGstin &&
      !/^[0-9A-Z]{15}$/.test(normalizeGstin(form.clientGstin))
    ) {
      return "Client GSTIN must contain 15 letters/numbers, or leave it blank";
    }
    if (!form.projectName) return "Select project";
    if (!form.documentDate) return "Select invoice date";
    if (!form.items?.length) return "Add at least one service";

    const invalidItem = form.items.some(
      (item) =>
        !cleanSectionName(item.section) ||
        !item.serviceName ||
        Number(item.quantity || 0) <= 0 ||
        Number(item.rate || 0) < 0,
    );

    if (invalidItem) return "Check service, quantity and rate";
    return "";
  };

  const save = async () => {
    const validationMessage = validate();

    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,

        company: "SVB",

        type: "invoice",

        number: String(form.invoiceNumber || "").trim(),

        invoiceNumber: String(form.invoiceNumber || "").trim(),

        client: form.client || null,
        clientGstin: normalizeGstin(form.clientGstin),

        bank: form.bank || null,

        terms: automaticTerms,

        /*
          Only one discount mode is sent
          to backend at a time.
        */
        overallDiscountType:
          form.discountMode === "total" ? form.overallDiscountType : "none",

        overallDiscountValue:
          form.discountMode === "total"
            ? Number(form.overallDiscountValue || 0)
            : 0,

        items: (form.items || []).map((item) => ({
          ...item,

          service: item.service || null,

          discountType:
            form.discountMode === "item" ? item.discountType : "none",

          discountValue:
            form.discountMode === "item" ? Number(item.discountValue || 0) : 0,
        })),
      };

      let savedDocument = null;

      if (editing) {
        const response = await api.put(`/sales/documents/${editing}`, payload, {
          headers,
        });

        savedDocument = response.data;
      } else {
        const response = await api.post("/sales/documents", payload, {
          headers,
        });

        savedDocument = response.data;
      }

      if (!savedDocument?._id) {
        throw new Error("Invoice saved, but invoice ID was not returned");
      }

      /*
        Important:
        Save the generated PDF immediately after
        the invoice record is saved.
        Finance can then open the invoice PDF directly.
      */
      try {
        await generateAndSaveInvoicePdf({
          documentId: savedDocument._id,

          documentData: {
            ...payload,
            ...savedDocument,

            client: savedDocument.client || payload.client,

            bank: savedDocument.bank || payload.bank,

            items: savedDocument.items?.length
              ? savedDocument.items
              : payload.items,

            terms: savedDocument.terms?.length
              ? savedDocument.terms
              : payload.terms,
          },
        });
      } catch (pdfError) {
        console.error("Invoice PDF auto-save error:", pdfError);

        alert(
          `Invoice saved successfully, but PDF could not be saved: ${
            pdfError?.response?.data?.message ||
            pdfError?.message ||
            "Unknown PDF error"
          }`,
        );
      }

      setOpen(false);
      reset();

      await load();
    } catch (error) {
      console.error("Save invoice error:", error);

      alert(
        error.response?.data?.message ||
          error.message ||
          "Failed to save invoice",
      );
    } finally {
      setSaving(false);
    }
  };

  const repairMissingInvoicePdf = async (document) => {
    if (!document?._id) {
      alert("Invoice ID not found.");
      return;
    }

    try {
      setDownloading(true);

      await generateAndSaveInvoicePdf({
        documentId: document._id,

        documentData: document,
      });

      alert("Invoice PDF generated and saved successfully.");

      setOpen(false);
      reset();

      await load();
    } catch (error) {
      console.error("Generate missing invoice PDF error:", error);

      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to generate invoice PDF",
      );
    } finally {
      setDownloading(false);
    }
  };

  const edit = (document) => {
    setEditing(document._id);
    setShowPreview(false);
    setForm({
      ...document,
      company: "SVB",
      invoiceNumber: document.invoiceNumber || document.number || "",
      client: document.client?._id || document.client || "",
      clientGstin: normalizeGstin(
        document.clientGstin ?? clientMasterGstin(document.client),
      ),
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => ({
            ...item,
            section: cleanSectionName(item.section || "General"),
          }))
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
    });
    setOpen(true);
  };

  const openPaymentModal = (document) => {
    const remaining = Math.max(
      0,
      Number(document.grandTotal || 0) - Number(document.paidAmount || 0),
    );

    setPaymentInvoice(document);
    setPaymentForm({
      paymentMethod: "NEFT",
      amount: remaining ? String(remaining) : String(document.grandTotal || ""),
      paymentDate: today(),
      senderName: "",
      senderBankName: "",
      senderAccountNumber: "",
      transactionNumber: "",
      chequeNumber: "",
      chequeDate: "",
      chequeBankName: "",
      remarks: "",
      proofFile: null,
    });
    setPaymentOpen(true);
  };

  const closePaymentModal = () => {
    if (paymentSaving) return;
    setPaymentOpen(false);
    setPaymentInvoice(null);
  };

  const savePayment = async () => {
    if (!paymentInvoice?._id) return;

    const amount = Number(paymentForm.amount || 0);

    if (amount <= 0) {
      alert("Enter received amount");
      return;
    }

    if (!paymentForm.paymentDate) {
      alert("Select payment date");
      return;
    }

    if (!String(paymentForm.senderName || "").trim()) {
      alert("Enter sender name");
      return;
    }

    if (!String(paymentForm.senderBankName || "").trim()) {
      alert("Enter sender bank name");
      return;
    }

    if (!paymentForm.proofFile) {
      alert("Payment proof screenshot/photo is required");
      return;
    }

    if (
      paymentForm.paymentMethod === "NEFT" &&
      !String(paymentForm.transactionNumber || "").trim()
    ) {
      alert("Enter NEFT / UTR / Transaction No.");
      return;
    }

    if (paymentForm.paymentMethod === "CHEQUE") {
      if (!String(paymentForm.chequeNumber || "").trim()) {
        alert("Enter cheque number");
        return;
      }

      if (!paymentForm.chequeDate) {
        alert("Select cheque date");
        return;
      }

      if (!String(paymentForm.chequeBankName || "").trim()) {
        alert("Enter cheque bank name");
        return;
      }
    }

    try {
      setPaymentSaving(true);

      const fd = new FormData();
      fd.append("paymentMethod", paymentForm.paymentMethod);
      fd.append("amount", String(amount));
      fd.append("paymentDate", paymentForm.paymentDate);
      fd.append("senderName", paymentForm.senderName.trim());
      fd.append("senderBankName", paymentForm.senderBankName.trim());
      fd.append(
        "senderAccountNumber",
        String(paymentForm.senderAccountNumber || "").trim(),
      );
      fd.append(
        "transactionNumber",
        String(paymentForm.transactionNumber || "").trim(),
      );
      fd.append("chequeNumber", String(paymentForm.chequeNumber || "").trim());
      fd.append("chequeDate", paymentForm.chequeDate || "");
      fd.append(
        "chequeBankName",
        String(paymentForm.chequeBankName || "").trim(),
      );
      fd.append("remarks", String(paymentForm.remarks || "").trim());
      fd.append("paymentProof", paymentForm.proofFile);

      await api.post(`/sales/documents/${paymentInvoice._id}/payments`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setPaymentOpen(false);
      setPaymentInvoice(null);
      await load();
      alert("Payment recorded successfully");
    } catch (error) {
      console.error("Record payment error:", error);
      alert(error.response?.data?.message || "Failed to record payment");
    } finally {
      setPaymentSaving(false);
    }
  };

  const remove = async (document) => {
    if (!window.confirm(`Delete ${document.number || "this invoice"}?`)) {
      return;
    }

    try {
      await api.delete(`/sales/documents/${document._id}`, { headers });
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete");
    }
  };

  const convertToInvoice = async (document) => {
    if (!window.confirm("Convert this Invoice to Invoice?")) return;

    try {
      await api.post(
        `/sales/documents/${document._id}/convert`,
        { type: "invoice" },
        { headers },
      );
      alert("Converted to Invoice successfully");
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Conversion failed");
    }
  };

  const openDocumentForPdf = (document) => {
    setEditing(document._id);
    setShowPreview(true);
    setForm({
      ...document,
      company: "SVB",
      invoiceNumber: document.invoiceNumber || document.number || "",
      client: document.client?._id || document.client || "",
      clientGstin: normalizeGstin(
        document.clientGstin ?? clientMasterGstin(document.client),
      ),
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => ({
            ...item,
            section: cleanSectionName(item.section || "General"),
          }))
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
      number: document.number || "",
    });
    setOpen(true);
  };

  const downloadPdf = async () => {
    if (!previewRef.current) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const imageHeight = (canvas.height * usableWidth) / canvas.width;

      let heightLeft = imageHeight;
      let position = margin;

      pdf.addImage(
        imageData,
        "PNG",
        margin,
        position,
        usableWidth,
        imageHeight,
      );

      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        position = heightLeft - imageHeight + margin;
        pdf.addPage();
        pdf.addImage(
          imageData,
          "PNG",
          margin,
          position,
          usableWidth,
          imageHeight,
        );
        heightLeft -= pageHeight - margin * 2;
      }

      const safeNumber = String(form.number || "INVOICE").replace(
        /[^\w-]+/g,
        "-",
      );

      pdf.save(`${safeNumber}.pdf`);
    } catch (error) {
      console.error("PDF error:", error);
      alert("PDF could not be generated");
    } finally {
      setDownloading(false);
    }
  };

  const itemAmount = (item) => {
    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;
    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.max(0, base - Math.min(discount, base));
  };

  const itemDiscountAmount = (item) => {
    if ((form.discountMode || "item") !== "item") return 0;

    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;

    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.min(Math.max(discount, 0), base);
  };

  const itemDiscountLabel = (item) => {
    if ((form.discountMode || "item") !== "item") return "-";

    const discountAmount = itemDiscountAmount(item);

    if (!discountAmount) return "-";

    if (item.discountType === "percentage") {
      return `${Number(item.discountValue || 0)}% (₹ ${money(discountAmount)})`;
    }

    if (item.discountType === "amount") {
      return `₹ ${money(discountAmount)}`;
    }

    return "-";
  };

  const itemTaxAmount = (item) => {
    if (form.taxMode === "NONE") return 0;

    const taxable = itemAmount(item);
    return (taxable * Number(item.gstRate || 0)) / 100;
  };

  const itemFinalAmount = (item) => itemAmount(item) + itemTaxAmount(item);

  const totalDiscountLabel = () => {
    if (form.discountMode !== "total" || !totals.overallDiscount) {
      return "";
    }

    if (form.overallDiscountType === "percentage") {
      return `${Number(form.overallDiscountValue || 0)}% (₹ ${money(
        totals.overallDiscount,
      )})`;
    }

    return `₹ ${money(totals.overallDiscount)}`;
  };

  return (
    <div className="pi-page">
      <div className="pi-topbar">
        <div>
          <h1>Invoices</h1>
          <p>Create, manage, preview and download professional invoices.</p>
        </div>

        <button
          className="pi-btn primary"
          onClick={() => {
            reset();
            setShowPreview(false);
            setOpen(true);
          }}
        >
          + Create Invoice
        </button>
      </div>

      <div className="invoice-filter-card">
        <div className="invoice-filter-head">
          <div>
            <h3>Invoice Overview</h3>
            <p>
              View invoices month-wise, quarterly, financial-year-wise or by
              custom date range.
            </p>
          </div>

          <button
            type="button"
            className="invoice-filter-clear"
            onClick={clearInvoiceFilters}
          >
            Reset View
          </button>
        </div>

        <div className="invoice-filter-grid">
          <label>
            View By
            <select
              value={invoiceFilters.viewBy}
              onChange={(e) =>
                setInvoiceFilters((previous) => ({
                  ...previous,
                  viewBy: e.target.value,
                }))
              }
            >
              <option value="month">Month Wise</option>
              <option value="quarter">Quarter Wise</option>
              <option value="year">Financial Year Wise</option>
              <option value="custom">Custom Date</option>
            </select>
          </label>

          {invoiceFilters.viewBy !== "custom" && (
            <label>
              Financial Year
              <select
                value={invoiceFilters.financialYear}
                onChange={(e) =>
                  setInvoiceFilters((previous) => ({
                    ...previous,
                    financialYear: e.target.value,
                  }))
                }
              >
                {invoiceFinancialYears.map((year) => (
                  <option key={year} value={year}>
                    {getFinancialYearLabel(year)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {invoiceFilters.viewBy === "month" && (
            <label>
              Month
              <select
                value={invoiceFilters.month}
                onChange={(e) =>
                  setInvoiceFilters((previous) => ({
                    ...previous,
                    month: e.target.value,
                  }))
                }
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {invoiceFilters.viewBy === "quarter" && (
            <label>
              Quarter
              <select
                value={invoiceFilters.quarter}
                onChange={(e) =>
                  setInvoiceFilters((previous) => ({
                    ...previous,
                    quarter: e.target.value,
                  }))
                }
              >
                <option value="1">Q1 (Apr - Jun)</option>
                <option value="2">Q2 (Jul - Sep)</option>
                <option value="3">Q3 (Oct - Dec)</option>
                <option value="4">Q4 (Jan - Mar)</option>
              </select>
            </label>
          )}

          {invoiceFilters.viewBy === "custom" && (
            <>
              <label>
                From Date
                <input
                  type="date"
                  value={invoiceFilters.fromDate}
                  onChange={(e) =>
                    setInvoiceFilters((previous) => ({
                      ...previous,
                      fromDate: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                To Date
                <input
                  type="date"
                  value={invoiceFilters.toDate}
                  min={invoiceFilters.fromDate || undefined}
                  onChange={(e) =>
                    setInvoiceFilters((previous) => ({
                      ...previous,
                      toDate: e.target.value,
                    }))
                  }
                />
              </label>
            </>
          )}
        </div>

        <div className="invoice-filter-summary">
          <div>
            <span>Invoices</span>
            <strong>{filteredDocs.length}</strong>
          </div>

          <div>
            <span>Invoice Value</span>
            <strong>₹ {money(filteredInvoiceValue)}</strong>
          </div>
        </div>
      </div>

      <div className="pi-card">
        {loading ? (
          <div className="pi-empty">Loading...</div>
        ) : docs.length === 0 ? (
          <div className="pi-empty">No invoices found.</div>
        ) : filteredDocs.length === 0 ? (
          <div className="pi-empty">
            No invoices found for the selected period.
          </div>
        ) : (
          <div className="pi-table-wrap">
            <table className="pi-table">
              <thead>
                <tr>
                  <th>Invoice No.</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((document) => (
                  <tr key={document._id}>
                    <td className="pi-number">{document.number}</td>
                    <td>{document.client?.companyName || "-"}</td>
                    <td>
                      {document.documentDate
                        ? new Date(document.documentDate).toLocaleDateString(
                            "en-IN",
                          )
                        : "-"}
                    </td>
                    <td>{document.projectName || "-"}</td>
                    <td>₹ {money(document.grandTotal)}</td>
                    <td>
                      <span
                        className={`pi-status ${
                          Number(
                            document.balanceAmount ?? document.grandTotal ?? 0,
                          ) <= 0 && Number(document.paidAmount || 0) > 0
                            ? "paid"
                            : Number(document.paidAmount || 0) > 0
                              ? "partial"
                              : ""
                        }`}
                      >
                        {Number(
                          document.balanceAmount ?? document.grandTotal ?? 0,
                        ) <= 0 && Number(document.paidAmount || 0) > 0
                          ? "Paid"
                          : Number(document.paidAmount || 0) > 0
                            ? "Partially Paid"
                            : document.status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <div className="pi-action-row">
                        <button
                          className="pi-link"
                          onClick={() => edit(document)}
                        >
                          Edit
                        </button>
                        <button
                          className="pi-link"
                          onClick={() => {
                            if (document.pdfAvailable) {
                              openDocumentForPdf(document);
                              return;
                            }

                            repairMissingInvoicePdf(document);
                          }}
                          disabled={downloading}
                        >
                          {document.pdfAvailable
                            ? "Preview / PDF"
                            : downloading
                              ? "Generating..."
                              : "Generate PDF"}
                        </button>
                        <button
                          className={`pi-link ${
                            Number(
                              document.balanceAmount ??
                                document.grandTotal ??
                                0,
                            ) <= 0
                              ? "paid-link"
                              : "payment-link"
                          }`}
                          onClick={() => openPaymentModal(document)}
                        >
                          {Number(
                            document.balanceAmount ?? document.grandTotal ?? 0,
                          ) <= 0
                            ? "Payment Details"
                            : "Mark Paid"}
                        </button>
                        <button
                          className="pi-link danger"
                          onClick={() => remove(document)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div className="pi-modal-overlay">
          <div className="pi-modal">
            <div className="pi-modal-head">
              <div>
                <h2>{editing ? "Edit" : "Create"} Invoice</h2>
                <p>
                  {showPreview
                    ? "Preview the final A4 Invoice."
                    : "Fill the Invoice details."}
                </p>
              </div>
              <div className="pi-modal-head-actions">
                <button
                  type="button"
                  className={`pi-mode-btn ${!showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(false)}
                >
                  Edit Form
                </button>

                <button
                  type="button"
                  className={`pi-mode-btn ${showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </button>

                <button className="pi-close" onClick={() => setOpen(false)}>
                  ✕
                </button>
              </div>
            </div>

            <div className="pi-editor">
              {!showPreview && (
                <div className="pi-form-panel">
                  <section className="pi-section">
                    <h3>Basic Information</h3>

                    <div className="pi-grid four">
                      <label>
                        Company
                        <input
                          type="text"
                          value="SVB"
                          readOnly
                          className="pi-readonly-input"
                        />
                      </label>

                      <label>
                        Invoice No.
                        <input
                          type="text"
                          value={form.invoiceNumber || ""}
                          placeholder="e.g. SVB/INV/2026/0001"
                          onChange={(e) =>
                            setForm({
                              ...form,
                              invoiceNumber: e.target.value,
                            })
                          }
                        />
                      </label>

                      <label>
                        Client
                        <SearchableSelect
                          value={form.client}
                          options={clients.map((client) => ({
                            value: client._id,
                            label:
                              client.companyName ||
                              client.name ||
                              "Unnamed Client",
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              client: value,
                              clientGstin: clientMasterGstin(
                                clients.find((client) => client._id === value),
                              ),
                              projectName: "",
                            })
                          }
                          placeholder="Search / Select Client"
                        />
                      </label>

                      <label>
                        Client GSTIN
                        <input
                          type="text"
                          value={form.clientGstin || ""}
                          maxLength={15}
                          placeholder="15-character GSTIN (optional)"
                          onChange={(e) =>
                            setForm((previous) => ({
                              ...previous,
                              clientGstin: normalizeGstin(e.target.value),
                            }))
                          }
                        />
                      </label>

                      <label>
                        Invoice Date
                        <input
                          type="date"
                          value={form.documentDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, documentDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Due Date
                        <input
                          type="date"
                          value={form.dueDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, dueDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Project
                        <SearchableSelect
                          value={form.projectName || ""}
                          options={filteredProjects.map((project) => ({
                            value: project.name,
                            label: `${project.name || "Unnamed Project"}${
                              project.category ? ` — ${project.category}` : ""
                            }`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              projectName: value,
                            })
                          }
                          placeholder="Search / Select Project"
                        />
                      </label>

                      <label>
                        Subject
                        <input
                          value={form.subject || ""}
                          placeholder="Subject / Scope"
                          onChange={(e) =>
                            setForm({ ...form, subject: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Bank
                        <SearchableSelect
                          value={form.bank || ""}
                          options={filteredBanks.map((bank) => ({
                            value: bank._id,
                            label: `${bank.bankName || "Bank"} - ${bank.accountNumber || ""}`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              bank: value,
                            })
                          }
                          placeholder="Search / Select Bank"
                        />
                      </label>

                      <label>
                        Tax Type
                        <select
                          value={form.taxMode}
                          onChange={(e) =>
                            setForm({ ...form, taxMode: e.target.value })
                          }
                        >
                          <option value="CGST_SGST">CGST + SGST</option>
                          <option value="IGST">IGST</option>
                          <option value="NONE">No GST</option>
                        </select>
                      </label>
                    </div>
                  </section>

                  <section className="pi-section">
                    <div className="pi-section-title-row">
                      <div>
                        <h3>Service Sections</h3>
                        <p className="pi-section-help">
                          Add Architecture, Interior, Structure, BIM or any
                          other service section in the same Invoice.
                        </p>
                      </div>
                    </div>

                    <div className="pi-add-section-bar">
                      <input
                        value={newSectionName}
                        placeholder="New section name e.g. Interior"
                        onChange={(e) => setNewSectionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSection();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={addSection}
                      >
                        + Add Section
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) =>
                                section.toLowerCase() === "architecture",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Architecture"),
                              ],
                            }));
                          }
                        }}
                      >
                        Architecture
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) => section.toLowerCase() === "interior",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [...previous.items, emptyItem("Interior")],
                            }));
                          }
                        }}
                      >
                        Interior
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) =>
                                section.toLowerCase() === "structure",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Structure"),
                              ],
                            }));
                          }
                        }}
                      >
                        Structure
                      </button>
                    </div>

                    <div className="pi-service-sections">
                      {sections.map((section) => {
                        const sectionRows = form.items
                          .map((item, index) => ({ item, index }))
                          .filter(
                            ({ item }) =>
                              cleanSectionName(item.section || "General") ===
                              section,
                          );

                        return (
                          <div className="pi-service-section" key={section}>
                            <div className="pi-service-section-head">
                              <div className="pi-section-name-wrap">
                                <span className="pi-section-badge">
                                  SECTION
                                </span>
                                <input
                                  className="pi-section-name-input"
                                  value={section}
                                  onChange={(e) =>
                                    renameSection(section, e.target.value)
                                  }
                                />
                              </div>

                              <div className="pi-service-section-actions">
                                <button
                                  type="button"
                                  className="pi-btn"
                                  onClick={() => addServiceToSection(section)}
                                >
                                  + Add Service
                                </button>

                                {sections.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-btn danger-outline"
                                    onClick={() => removeSection(section)}
                                  >
                                    Remove Section
                                  </button>
                                )}
                              </div>
                            </div>

                            {sectionRows.map(({ item, index }) => (
                              <div
                                className="pi-item-card"
                                key={`${section}-${index}`}
                              >
                                <div className="pi-grid item-grid">
                                  <label className="wide">
                                    Service
                                    <SearchableSelect
                                      value={item.service || ""}
                                      options={services.map((service) => ({
                                        value: service._id,
                                        label:
                                          service.name || "Unnamed Service",
                                      }))}
                                      onChange={(value) =>
                                        chooseService(index, value)
                                      }
                                      placeholder="Search / Select Service"
                                    />
                                  </label>

                                  <label>
                                    Qty
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          quantity: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    Unit
                                    <select
                                      value={item.unit || "Sq. Ft."}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          unit: e.target.value,
                                        })
                                      }
                                    >
                                      {UNIT_OPTIONS.map((unit) => (
                                        <option key={unit} value={unit}>
                                          {unit}
                                        </option>
                                      ))}
                                    </select>
                                  </label>

                                  <label>
                                    Rate
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.rate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          rate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  {form.discountMode === "item" ? (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-input">
                                        <select
                                          value={item.discountType}
                                          onChange={(e) =>
                                            updateItem(index, {
                                              discountType: e.target.value,
                                            })
                                          }
                                        >
                                          <option value="none">None</option>
                                          <option value="percentage">%</option>
                                          <option value="amount">₹</option>
                                        </select>

                                        <input
                                          type="number"
                                          min="0"
                                          value={item.discountValue}
                                          onChange={(e) =>
                                            updateItem(index, {
                                              discountValue: Number(
                                                e.target.value,
                                              ),
                                            })
                                          }
                                        />
                                      </div>
                                    </label>
                                  ) : (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-disabled">
                                        Total discount selected
                                      </div>
                                    </label>
                                  )}

                                  <label>
                                    HSN / SAC Code
                                    <input
                                      type="text"
                                      value={item.hsnCode || ""}
                                      placeholder="e.g. 998321"
                                      onChange={(e) =>
                                        updateItem(index, {
                                          hsnCode: e.target.value,
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    GST %
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.gstRate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          gstRate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    Amount
                                    <div className="pi-readonly-amount">
                                      ₹ {money(itemFinalAmount(item))}
                                    </div>
                                  </label>
                                </div>

                                <label className="pi-description-label">
                                  Description
                                  <textarea
                                    value={item.description || ""}
                                    onChange={(e) =>
                                      updateItem(index, {
                                        description: e.target.value,
                                      })
                                    }
                                  />
                                </label>

                                {sectionRows.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-remove-item"
                                    onClick={() =>
                                      setForm((previous) => ({
                                        ...previous,
                                        items: previous.items.filter(
                                          (_, itemIndex) => itemIndex !== index,
                                        ),
                                      }))
                                    }
                                  >
                                    Remove Service
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  <section className="pi-section">
                    <h3>Discount & Terms</h3>

                    <div className="pi-discount-mode-box">
                      <div className="pi-discount-mode-title">
                        Apply Discount On
                      </div>

                      <div className="pi-discount-mode-options">
                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "item" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="item"
                            checked={form.discountMode === "item"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "item",
                                overallDiscountType: "none",
                                overallDiscountValue: 0,
                              }))
                            }
                          />
                          <div>
                            <strong>Item Wise Discount</strong>
                            <span>
                              Discount can be different for each service item.
                            </span>
                          </div>
                        </label>

                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "total" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="total"
                            checked={form.discountMode === "total"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "total",
                                items: previous.items.map((item) => ({
                                  ...item,
                                  discountType: "none",
                                  discountValue: 0,
                                })),
                              }))
                            }
                          />
                          <div>
                            <strong>Total Invoice Discount</strong>
                            <span>
                              One discount is applied to the total service
                              value.
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {form.discountMode === "total" && (
                      <div className="pi-grid two pi-total-discount-fields">
                        <label>
                          Total Discount Type
                          <select
                            value={form.overallDiscountType}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                overallDiscountType: e.target.value,
                              })
                            }
                          >
                            <option value="none">No Discount</option>
                            <option value="percentage">Percentage (%)</option>
                            <option value="amount">Fixed Amount (₹)</option>
                          </select>
                        </label>

                        <label>
                          Total Discount Value
                          <input
                            type="number"
                            min="0"
                            value={form.overallDiscountValue}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                overallDiscountValue: Number(e.target.value),
                              })
                            }
                          />
                        </label>
                      </div>
                    )}

                    <div className="pi-auto-terms">
                      <div className="pi-auto-terms-head">
                        <div>
                          <h4> Terms & Conditions</h4>
                        </div>
                      </div>

                      {automaticTerms.length ? (
                        <ol className="pi-continuous-terms-list form-list">
                          {automaticTerms.map((term, index) => (
                            <li key={index}>{term}</li>
                          ))}
                        </ol>
                      ) : (
                        <div className="pi-no-auto-terms">
                          Add Terms & Conditions in Terms Master for
                          Architecture, Interior or Structure.
                        </div>
                      )}
                    </div>
                  </section>

                  <div className="pi-form-actions">
                    <button
                      className="pi-btn"
                      type="button"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </button>

                    <button
                      className="pi-btn preview"
                      type="button"
                      onClick={() => setShowPreview(true)}
                    >
                      Preview
                    </button>

                    <button
                      className="pi-btn primary"
                      type="button"
                      onClick={save}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Invoice"}
                    </button>
                  </div>
                </div>
              )}

              {showPreview && (
                <div className="pi-preview-panel preview-only">
                  <div className="pi-preview-toolbar">
                    <div>
                      <div className="pi-preview-label">A4 Preview</div>
                      <span>Review before saving or downloading PDF.</span>
                    </div>

                    <div className="pi-preview-toolbar-actions">
                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => setShowPreview(false)}
                      >
                        ← Back to Edit
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={downloadPdf}
                        disabled={downloading}
                      >
                        {downloading ? "Generating PDF..." : "Download PDF"}
                      </button>

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={save}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Invoice"}
                      </button>
                    </div>
                  </div>

                  <div className="pi-preview-stage">
                    <div className="pi-a4" ref={previewRef}>
                      <header className="pi-doc-header">
                        <div className="pi-brand">
                          {logo ? (
                            <img
                              src={logo}
                              alt="Company logo"
                              className="pi-logo"
                            />
                          ) : (
                            <div className="pi-logo-placeholder">LOGO</div>
                          )}

                          <div>
                            <h2>{companyInfo.name}</h2>
                            <p>{companyInfo.address}</p>
                            {companyInfo.email && <p>{companyInfo.email}</p>}
                            {companyInfo.phone && <p>{companyInfo.phone}</p>}
                            {companyInfo.gstin && (
                              <p>GSTIN: {companyInfo.gstin}</p>
                            )}
                          </div>
                        </div>

                        <div className="pi-doc-title">
                          <h1>TAX INVOICE</h1>
                          <p>
                            <strong>Invoice No:</strong>{" "}
                            {form.invoiceNumber || form.number || "-"}
                          </p>
                          <p>
                            <strong>Date:</strong> {form.documentDate || "-"}
                          </p>
                          <p>
                            <strong>Due Date:</strong> {form.dueDate || "-"}
                          </p>
                        </div>
                      </header>

                      <div className="pi-blue-line" />

                      <section className="pi-party-grid">
                        <div>
                          <span className="pi-small-title">BILL TO</span>
                          <h3>
                            {selectedClient?.companyName || "Select Client"}
                          </h3>
                          <p>{selectedClient?.contactPerson || ""}</p>
                          <p>{formatAddress(selectedClient?.billingAddress)}</p>
                          <p>{selectedClient?.email || ""}</p>
                          <p>{selectedClient?.phone || ""}</p>
                          {form.clientGstin && <p>GSTIN: {form.clientGstin}</p>}
                        </div>

                        <div className="pi-project-box">
                          <p>
                            <strong>Project:</strong> {form.projectName || "-"}
                          </p>

                          <p>
                            <strong>Subject:</strong> {form.subject || "-"}
                          </p>
                        </div>
                      </section>

                      <div className="inv-doc-items">
                        <div className="inv-doc-items-head">
                          <div>#</div>
                          <div>Service / Description</div>
                          <div>HSN/SAC</div>
                          <div>Qty</div>
                          <div>Unit</div>
                          <div>Unit Rate</div>
                          <div>Discount</div>
                          <div>GST</div>
                          <div>Amount</div>
                        </div>

                        {sections.map((section) => {
                          const sectionRows = form.items
                            .map((item, index) => ({ item, index }))
                            .filter(
                              ({ item }) =>
                                cleanSectionName(item.section || "General") ===
                                section,
                            );

                          const sectionSubtotal = sectionRows.reduce(
                            (sum, { item }) => sum + itemAmount(item),
                            0,
                          );

                          return (
                            <div
                              className="inv-doc-service-section"
                              key={section}
                            >
                              <div className="inv-doc-section-title">
                                {section}
                              </div>

                              {sectionRows.map(({ item, index }) => (
                                <div
                                  className="inv-doc-item-row"
                                  key={`${section}-${index}`}
                                >
                                  <div>{index + 1}</div>

                                  <div className="inv-doc-service-cell">
                                    <strong>
                                      {item.serviceName || "Service"}
                                    </strong>

                                    {item.description && (
                                      <p>{item.description}</p>
                                    )}
                                  </div>

                                  <div>{item.hsnCode || "-"}</div>

                                  <div>{item.quantity}</div>

                                  <div>{item.unit}</div>

                                  <div className="inv-doc-number-cell">
                                    ₹ {money(item.rate)}
                                  </div>

                                  <div className="inv-doc-discount-cell">
                                    {itemDiscountLabel(item)}
                                  </div>

                                  <div className="inv-doc-gst-cell">
                                    {Number(item.gstRate || 0)}%
                                  </div>

                                  <div className="inv-doc-number-cell">
                                    ₹ {money(itemFinalAmount(item))}
                                  </div>
                                </div>
                              ))}

                              <div className="inv-doc-section-total">
                                <span>Section Subtotal</span>
                                <strong>₹ {money(sectionSubtotal)}</strong>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <section className="pi-doc-bottom">
                        <div className="pi-terms-box">
                          <h4>Terms & Conditions</h4>

                          {automaticTerms.length ? (
                            <ol className="pi-continuous-terms-list">
                              {automaticTerms.map((term, index) => (
                                <li key={index}>{term}</li>
                              ))}
                            </ol>
                          ) : (
                            <p>
                              Terms will appear automatically from Terms Master.
                            </p>
                          )}
                        </div>

                        <div className="pi-total-box">
                          <div>
                            <span>Subtotal</span>
                            <b>₹ {money(totals.subtotal)}</b>
                          </div>
                          {form.discountMode === "item" &&
                            totals.itemDiscount > 0 && (
                              <div>
                                <span>Item Discount</span>
                                <b>- ₹ {money(totals.itemDiscount)}</b>
                              </div>
                            )}

                          {form.discountMode === "total" &&
                            totals.overallDiscount > 0 && (
                              <div>
                                <span>Total Discount</span>
                                <b>- {totalDiscountLabel()}</b>
                              </div>
                            )}
                          <div>
                            <span>Taxable Amount</span>
                            <b>₹ {money(totals.taxableAmount)}</b>
                          </div>

                          {form.taxMode === "CGST_SGST" && (
                            <>
                              <div>
                                <span>CGST</span>
                                <b>₹ {money(totals.cgst)}</b>
                              </div>
                              <div>
                                <span>SGST</span>
                                <b>₹ {money(totals.sgst)}</b>
                              </div>
                            </>
                          )}

                          {form.taxMode === "IGST" && (
                            <div>
                              <span>IGST</span>
                              <b>₹ {money(totals.igst)}</b>
                            </div>
                          )}

                          <div className="pi-grand-total">
                            <span>Grand Total</span>
                            <b>₹ {money(totals.grandTotal)}</b>
                          </div>
                          {Number(form.paidAmount || 0) > 0 && (
                            <>
                              <div>
                                <span>Paid Amount</span>
                                <b>₹ {money(form.paidAmount)}</b>
                              </div>
                              <div>
                                <span>Balance</span>
                                <b>
                                  ₹{" "}
                                  {money(
                                    Math.max(
                                      0,
                                      totals.grandTotal -
                                        Number(form.paidAmount || 0),
                                    ),
                                  )}
                                </b>
                              </div>
                            </>
                          )}
                        </div>
                      </section>

                      <section className="pi-bank-box">
                        <h4>Bank Details</h4>
                        {selectedBank ? (
                          <div className="pi-bank-grid">
                            <p>
                              <strong>Bank:</strong>{" "}
                              {selectedBank.bankName || "-"}
                            </p>
                            <p>
                              <strong>A/C Name:</strong>{" "}
                              {selectedBank.accountName || "-"}
                            </p>
                            <p>
                              <strong>A/C No:</strong>{" "}
                              {selectedBank.accountNumber || "-"}
                            </p>
                            <p>
                              <strong>IFSC:</strong> {selectedBank.ifsc || "-"}
                            </p>
                            {selectedBank.swiftCode && (
                              <p>
                                <strong>SWIFT:</strong> {selectedBank.swiftCode}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p>Select a bank account.</p>
                        )}
                      </section>

                      <footer className="pi-doc-footer">
                        <div>
                          <p>
                            This document is a Tax Invoice. Payment status is
                            subject to actual receipt and verification.
                          </p>
                        </div>

                        <div className="pi-signature">
                          <img
                            src={authorizedSignature}
                            alt="Authorized Signatory"
                            className="pi-signature-image"
                          />
                          <div className="pi-sign-line" />
                          <strong>Authorized Signatory</strong>
                          <span>{companyInfo.name}</span>
                        </div>
                      </footer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {paymentOpen && paymentInvoice && (
        <div className="invoice-payment-overlay">
          <div className="invoice-payment-modal">
            <div className="invoice-payment-head">
              <div>
                <h2>
                  {Number(
                    paymentInvoice.balanceAmount ??
                      paymentInvoice.grandTotal ??
                      0,
                  ) <= 0
                    ? "Payment Details"
                    : "Record Payment"}
                </h2>
                <p>
                  Invoice: <strong>{paymentInvoice.number || "-"}</strong>
                </p>
              </div>

              <button
                type="button"
                className="invoice-payment-close"
                onClick={closePaymentModal}
              >
                ✕
              </button>
            </div>

            <div className="invoice-payment-summary">
              <div>
                <span>Invoice Total</span>
                <strong>₹ {money(paymentInvoice.grandTotal)}</strong>
              </div>
              <div>
                <span>Paid</span>
                <strong>₹ {money(paymentInvoice.paidAmount)}</strong>
              </div>
              <div>
                <span>Balance</span>
                <strong>
                  ₹{" "}
                  {money(
                    Math.max(
                      0,
                      Number(paymentInvoice.grandTotal || 0) -
                        Number(paymentInvoice.paidAmount || 0),
                    ),
                  )}
                </strong>
              </div>
            </div>

            {Number(
              paymentInvoice.balanceAmount ?? paymentInvoice.grandTotal ?? 0,
            ) > 0 ? (
              <>
                <div className="invoice-payment-grid">
                  <label>
                    Payment Method *
                    <select
                      value={paymentForm.paymentMethod}
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          paymentMethod: e.target.value,
                          transactionNumber: "",
                          chequeNumber: "",
                          chequeDate: "",
                          chequeBankName: "",
                        }))
                      }
                    >
                      <option value="NEFT">NEFT / Bank Transfer</option>
                      <option value="CHEQUE">Cheque</option>
                    </select>
                  </label>

                  <label>
                    Received Amount *
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          amount: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Payment Date *
                    <input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          paymentDate: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender / Payer Name *
                    <input
                      value={paymentForm.senderName}
                      placeholder="Person / Company name"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderName: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender Bank Name *
                    <input
                      value={paymentForm.senderBankName}
                      placeholder="Bank name"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderBankName: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Sender Account No.
                    <input
                      value={paymentForm.senderAccountNumber}
                      placeholder="Optional"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          senderAccountNumber: e.target.value,
                        }))
                      }
                    />
                  </label>

                  {paymentForm.paymentMethod === "NEFT" ? (
                    <label className="invoice-payment-span-2">
                      UTR / Transaction No. *
                      <input
                        value={paymentForm.transactionNumber}
                        placeholder="Enter NEFT / UTR / Transaction reference"
                        onChange={(e) =>
                          setPaymentForm((previous) => ({
                            ...previous,
                            transactionNumber: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ) : (
                    <>
                      <label>
                        Cheque No. *
                        <input
                          value={paymentForm.chequeNumber}
                          placeholder="Cheque number"
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeNumber: e.target.value,
                            }))
                          }
                        />
                      </label>

                      <label>
                        Cheque Date *
                        <input
                          type="date"
                          value={paymentForm.chequeDate}
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeDate: e.target.value,
                            }))
                          }
                        />
                      </label>

                      <label className="invoice-payment-span-2">
                        Cheque Bank Name *
                        <input
                          value={paymentForm.chequeBankName}
                          placeholder="Bank printed on cheque"
                          onChange={(e) =>
                            setPaymentForm((previous) => ({
                              ...previous,
                              chequeBankName: e.target.value,
                            }))
                          }
                        />
                      </label>
                    </>
                  )}

                  <label className="invoice-payment-span-2">
                    Payment Proof *
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          proofFile: e.target.files?.[0] || null,
                        }))
                      }
                    />
                    <small>
                      NEFT screenshot / bank receipt or cheque photo is
                      mandatory.
                    </small>
                  </label>

                  <label className="invoice-payment-span-2">
                    Remarks
                    <textarea
                      value={paymentForm.remarks}
                      placeholder="Optional payment note"
                      onChange={(e) =>
                        setPaymentForm((previous) => ({
                          ...previous,
                          remarks: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="invoice-payment-actions">
                  <button
                    type="button"
                    className="pi-btn"
                    onClick={closePaymentModal}
                    disabled={paymentSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="pi-btn primary"
                    onClick={savePayment}
                    disabled={paymentSaving}
                  >
                    {paymentSaving ? "Saving Payment..." : "Save Payment"}
                  </button>
                </div>
              </>
            ) : (
              <div className="invoice-payment-history">
                <h3>Payment History</h3>

                {Array.isArray(paymentInvoice.payments) &&
                paymentInvoice.payments.length ? (
                  paymentInvoice.payments.map((payment, index) => (
                    <div className="invoice-payment-history-card" key={index}>
                      <div>
                        <span>Method</span>
                        <strong>{payment.paymentMethod || "-"}</strong>
                      </div>
                      <div>
                        <span>Amount</span>
                        <strong>₹ {money(payment.amount)}</strong>
                      </div>
                      <div>
                        <span>Date</span>
                        <strong>
                          {payment.paymentDate
                            ? new Date(payment.paymentDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "-"}
                        </strong>
                      </div>
                      <div>
                        <span>Sender</span>
                        <strong>{payment.senderName || "-"}</strong>
                      </div>
                      <div>
                        <span>Bank</span>
                        <strong>{payment.senderBankName || "-"}</strong>
                      </div>
                      <div>
                        <span>Reference</span>
                        <strong>
                          {payment.paymentMethod === "CHEQUE"
                            ? payment.chequeNumber || "-"
                            : payment.transactionNumber || "-"}
                        </strong>
                      </div>

                      {payment.proofUrl && (
                        <div className="invoice-payment-proof-row">
                          <span>Proof</span>
                          <div className="invoice-payment-proof-row">
                            <span>Payment Proof</span>

                            <a
                              href={getPaymentProofUrl(payment.proofUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={getPaymentProofUrl(payment.proofUrl)}
                                alt="Payment Proof"
                                className="invoice-payment-proof-image"
                              />
                            </a>

                            <a
                              href={getPaymentProofUrl(payment.proofUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View Full Proof
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p>No payment history available.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePage;
